/**
 * ACWTimer Component
 *
 * Combined After Call Work timer with disposition selection.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AcwDispositionForm } from './AcwDispositionForm';
import { AcwTimerProgress } from './AcwTimerProgress';
import { AcwTimerHeader } from './AcwTimerHeader';
import { AcwTimerActions } from './AcwTimerActions';
import { ACWTimerProps } from './acw.types';
import { useAcwCountdown } from './useAcwCountdown';
import { useAcwDispositions } from './useAcwDispositions';
import styles from './ACWTimer.module.css';

export type { ACWResult } from './acw.types';

export function ACWTimer({
  duration,
  skillName,
  skillId,
  contactId,
  openedSapContacts = [],
  requireDisposition = false,
  onComplete,
  onExtend,
}: ACWTimerProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [selectedDispositionId, setSelectedDispositionId] = useState('');
  const [selectedSapContactId, setSelectedSapContactId] = useState('');

  const { dispositions, isLoadingDispositions, dispositionError } = useAcwDispositions(
    skillId,
    contactId
  );

  const selectedDisposition = dispositions.find((d) => d.dispositionId === selectedDispositionId);

  useEffect(() => {
    if (openedSapContacts.length > 0 && !selectedSapContactId) {
      setSelectedSapContactId(openedSapContacts[0].id);
    }
  }, [openedSapContacts, selectedSapContactId]);

  const {
    remaining,
    isExtending,
    isSaving,
    canGoAvailable,
    isWarning,
    progressPercent,
    handleGoAvailable,
    handleExtend,
  } = useAcwCountdown({
    duration,
    requireDisposition,
    selectedDispositionId,
    selectedDisposition,
    notes,
    selectedSapContactId,
    onComplete,
    onExtend,
  });

  return (
    <div className={styles.container}>
      {isSaving && (
        <div className={styles.savingOverlay}>
          <div className={styles.spinner} />
          <p>{t('acw:timer.savingDisposition')}</p>
        </div>
      )}

      <AcwTimerHeader skillName={skillName} duration={duration} />

      <div className={styles.card}>
        <AcwTimerProgress
          remaining={remaining}
          isWarning={isWarning}
          progressPercent={progressPercent}
        />

        <AcwDispositionForm
          dispositions={dispositions}
          isLoadingDispositions={isLoadingDispositions}
          dispositionError={dispositionError}
          selectedDispositionId={selectedDispositionId}
          onDispositionSelect={setSelectedDispositionId}
          openedSapContacts={openedSapContacts}
          selectedSapContactId={selectedSapContactId}
          onSapContactSelect={setSelectedSapContactId}
          notes={notes}
          onNotesChange={setNotes}
          requiresNotes={!!selectedDisposition?.requiresNotes}
        />

        <AcwTimerActions
          canGoAvailable={canGoAvailable}
          isSaving={isSaving}
          isExtending={isExtending}
          requireDisposition={requireDisposition}
          selectedDispositionId={selectedDispositionId}
          isWarning={isWarning}
          onGoAvailable={handleGoAvailable}
          onExtend={onExtend ? handleExtend : undefined}
        />
      </div>
    </div>
  );
}

export default ACWTimer;
